import React, { useState } from 'react';
import { FaTimes, FaQuestionCircle, FaCode } from 'react-icons/fa';

const SingleQuesModal = ({ onClose, onSubmit, categoryId }) => {
    const [questionType, setQuestionType] = useState(null); // State to track question type
    const [questionText, setQuestionText] = useState('');
    const [points, setPoints] = useState(10); // Default points
    const [options, setOptions] = useState([{ option_text: '', is_correct: false }]); // Initial option
    const [programmingDetails, setProgrammingDetails] = useState({
        programming_language: '',
        input_format: '',
        output_format: '',
        sample_input: '',
        sample_output: '',
        time_limit: '',
        memory_limit: '',
        test_cases: [{ input_data: '', expected_output: '' }],
    });

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...options];
        newOptions[index][field] = value;
        setOptions(newOptions);
    };

    const handleAddOption = () => {
        setOptions([...options, { option_text: '', is_correct: false }]);
    };

    const handleRemoveOption = (index) => {
        const newOptions = options.filter((_, i) => i !== index);
        setOptions(newOptions);
    };

    const handleProgrammingDetailChange = (field, value) => {
        setProgrammingDetails({ ...programmingDetails, [field]: value });
    };

    const handleTestCaseChange = (index, field, value) => {
        const newTestCases = [...programmingDetails.test_cases];
        newTestCases[index][field] = value;
        setProgrammingDetails({ ...programmingDetails, test_cases: newTestCases });
    };

    const handleAddTestCase = () => {
        setProgrammingDetails({
            ...programmingDetails,
            test_cases: [...programmingDetails.test_cases, { input_data: '', expected_output: '' }],
        });
    };

    const handleRemoveTestCase = (index) => {
        const newTestCases = programmingDetails.test_cases.filter((_, i) => i !== index);
        setProgrammingDetails({ ...programmingDetails, test_cases: newTestCases });
    };

    const handleSubmit = () => {
        const questionData = {
            category_id: categoryId,
            question_text: questionText,
            points: points,
            question_type: questionType,
            ...(questionType === 'MCQ' && { options: options }),
            ...(questionType === 'Programming' && { programming_details: programmingDetails }),
        };
        onSubmit(questionData);
        onClose();
    };

    return (
        <div className="p-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Select Question Type</h3>
                {/* <button className="text-gray-500" onClick={onClose}>
                    <FaTimes size={20} />
                </button> */}
            </div>

            {!questionType ? (
                <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div 
                        className="flex flex-col items-center p-4 border rounded-lg shadow-md cursor-pointer hover:shadow-lg transition"
                        onClick={() => setQuestionType('MCQ')}
                    >
                        <FaQuestionCircle size={40} className="text-red-500 mb-2" />
                        <h4 className="text-md font-medium text-center">Multiple Choice Question (MCQ)</h4>
                        <p className="text-sm text-gray-600 text-center">Choose this for questions with multiple options.</p>
                    </div>
                    <div 
                        className="flex flex-col items-center p-4 border rounded-lg shadow-md cursor-pointer hover:shadow-lg transition"
                        onClick={() => setQuestionType('Programming')}
                    >
                        <FaCode size={40} className="text-red-500 mb-2" />
                        <h4 className="text-md font-medium text-center">Programming Question</h4>
                        <p className="text-sm text-gray-600 text-center">Choose this for coding-related questions.</p>
                    </div>
                </div>
            ) : questionType === 'MCQ' ? (
                <>
                    {/* Question Text */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Question Text</label>
                        <textarea 
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            rows="3"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter your question here..."
                        />
                    </div>

                    {/* Points */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Points</label>
                        <input 
                            type="number"
                            value={points}
                            onChange={(e) => setPoints(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter points for the question"
                        />
                    </div>

                    {/* Options */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Options</label>
                        {options.map((option, index) => (
                            <div key={index} className="flex items-center mb-2">
                                <input 
                                    type="text"
                                    value={option.option_text}
                                    onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                                    placeholder={`Option ${index + 1}`}
                                />
                                <input 
                                    type="checkbox"
                                    checked={option.is_correct}
                                    onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                                    className="ml-2"
                                />
                                <span className="ml-1">Correct</span>
                                <button 
                                    type="button"
                                    onClick={() => handleRemoveOption(index)}
                                    className="ml-2 text-red-500"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button 
                            type="button"
                            onClick={handleAddOption}
                            className="bg-gray-500 text-white p-2 rounded mt-2"
                        >
                            Add Option
                        </button>
                    </div>

                    <div className="flex justify-end mt-4">
                        <button className="bg-gray-500 text-white p-2 rounded mr-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="bg-red-500 text-white p-2 rounded" onClick={handleSubmit}>
                            Submit
                        </button>
                    </div>
                </>
            ) : (
                // Programming Question Fields
                <>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Question Text</label>
                        <textarea 
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            rows="3"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter your question here..."
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Points</label>
                        <input 
                            type="number"
                            value={points}
                            onChange={(e) => setPoints(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter points for the question"
                        />
                    </div>

                    {/* Programming Details */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Programming Language</label>
                        <input 
                            type="text"
                            value={programmingDetails.programming_language}
                            onChange={(e) => handleProgrammingDetailChange('programming_language', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter programming language"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Input Format</label>
                        <input 
                            type="text"
                            value={programmingDetails.input_format}
                            onChange={(e) => handleProgrammingDetailChange('input_format', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter input format"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Output Format</label>
                        <input 
                            type="text"
                            value={programmingDetails.output_format}
                            onChange={(e) => handleProgrammingDetailChange('output_format', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter output format"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Sample Input</label>
                        <input 
                            type="text"
                            value={programmingDetails.sample_input}
                            onChange={(e) => handleProgrammingDetailChange('sample_input', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter sample input"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Sample Output</label>
                        <input 
                            type="text"
                            value={programmingDetails.sample_output}
                            onChange={(e) => handleProgrammingDetailChange('sample_output', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter sample output"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Time Limit (ms)</label>
                        <input 
                            type="number"
                            value={programmingDetails.time_limit}
                            onChange={(e) => handleProgrammingDetailChange('time_limit', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter time limit"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Memory Limit (MB)</label>
                        <input 
                            type="number"
                            value={programmingDetails.memory_limit}
                            onChange={(e) => handleProgrammingDetailChange('memory_limit', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                            placeholder="Enter memory limit"
                        />
                    </div>

                    {/* Test Cases */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Test Cases</label>
                        {programmingDetails.test_cases.map((testCase, index) => (
                            <div key={index} className="flex mb-2">
                                <input 
                                    type="text"
                                    value={testCase.input_data}
                                    onChange={(e) => handleTestCaseChange(index, 'input_data', e.target.value)}
                                    className="mt-1 block w-1/2 border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2 mr-2"
                                    placeholder="Input Data"
                                />
                                <input 
                                    type="text"
                                    value={testCase.expected_output}
                                    onChange={(e) => handleTestCaseChange(index, 'expected_output', e.target.value)}
                                    className="mt-1 block w-1/2 border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50 p-2"
                                    placeholder="Expected Output"
                                />
                                <button 
                                    type="button"
                                    onClick={() => handleRemoveTestCase(index)}
                                    className="ml-2 text-red-500"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button 
                            type="button"
                            onClick={handleAddTestCase}
                            className="bg-gray-500 text-white p-2 rounded mt-2"
                        >
                            Add Test Case
                        </button>
                    </div>

                    <div className="flex justify-end mt-4">
                        <button className="bg-gray-500 text-white p-2 rounded mr-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="bg-red-500 text-white p-2 rounded" onClick={handleSubmit}>
                            Submit
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default SingleQuesModal;